import LogRocket from 'logrocket'
import { setContext } from './sentry'

/**
 * Initialize LogRocket with application settings
 * @returns {Promise<void>}
 */
export const init = async () => {
  LogRocket.init(import.meta.env.VITE_LOG_ROCKET_CODE, {
    mergeIframes: true,
    childDomains: ['*'],
  })
  LogRocket.getSessionURL((sessionURL) => {
    setContext('LogRocket', { sessionURL })
  })
}

/**
 * Identify a user in LogRocket with selected context
 * @param {string} userId - Unique user identifier
 * @param {Object} userData - User information and metadata
 * @param {string} userData.name - User's full name
 * @param {string} userData.email - User's email address
 * @param {string} userData.accountId - Associated account/company ID
 * @param {string} userData.accountName - Associated account/company name
 * @param {boolean} userData.isAdmin - Whether user has admin privileges
 * @param {boolean} userData.isInternal - Whether this is an internal user
 * @param {string} userData.actorClass - Coarse audience classification
 */
export const identify = (userId, userData) => {
  LogRocket.identify(String(userId), {
    // Core identification
    userId: String(userId),
    actorClass: userData.actorClass,
    name: userData.name,
    email: userData.email,

    // Account context
    accountId: userData.accountId,
    accountName: userData.accountName,

    // User permissions and role
    isAdmin: userData.isAdmin,
    isInternal: userData.isInternal,
    userRole: userData.userRole || 'unknown',

    // Feature and product context
    planTier: userData.planTier,
    features: userData.features || [],

    // Engagement metrics
    portfolioCount: userData.portfolioCount || 0,
    integrationTypes: userData.integrationTypes || [],
    lastMessageDate: userData.lastMessageDate,
  })
}

/**
 * Track a custom event in LogRocket
 * @param {string} label - Event name/identifier
 */
export const track = (label, context) => {
  LogRocket.track(label, context)
}

export const startNewSession = () => LogRocket.startNewSession()

/**
 * Add custom data to LogRocket session for debugging
 * @param {string} category - Category/namespace for the data
 * @param {Object} data - Data object to track
 */
export const addMetadata = (category, data) => {
  LogRocket.getSessionURL((sessionURL) => {
    setContext(category, {
      ...data,
      sessionURL,
    })
  })
}

/**
 * Capture user frustration signals
 * @param {string} frustrationType - Type of frustration (rage_click, dead_click, error_click, etc.)
 * @param {Object} context - Additional context about the frustration
 */
export const trackFrustration = (frustrationType, context = {}) => {
  LogRocket.track('USER_FRUSTRATION', {
    type: frustrationType,
    timestamp: new Date().toISOString(),
    ...context,
  })
}

/**
 * Record performance metrics
 * @param {string} operationName - Name of the operation being measured
 * @param {number} duration - Duration in milliseconds
 * @param {Object} metadata - Additional metadata about the operation
 */
export const trackPerformance = (operationName, duration, metadata = {}) => {
  LogRocket.track('PERFORMANCE_METRIC', {
    operation: operationName,
    duration,
    isSlow: duration > 2000,
    timestamp: new Date().toISOString(),
    ...metadata,
  })
}
